import { useState, useMemo, useRef, useEffect, Fragment } from 'react';
import { Trash2, Search } from 'lucide-react';
import {
  DOODLE_PACKS,
  getAllDoodleEntriesWithPack,
  getResolvedDoodleUrl,
  getDoodleDisplayUrl,
  type DoodleEntry,
  type DoodlePack,
} from '../../lib/image-editor/doodleCatalog';
import { getApiBase } from '../../lib/api';
import type { UseImageEditorResult } from '../../hooks/useImageEditor';
import type { IconShape } from '../../lib/image-editor/imagePipeline';
import { ICON_QR_SIZE_MIN } from '../../lib/image-editor/overlayLimits';

const DOODLE_SHAPE_OPTIONS: { value: IconShape; label: string }[] = [
  { value: 'none', label: '無' },
  { value: 'circle', label: '圓形' },
  { value: 'square', label: '方形' },
  { value: 'rounded', label: '圓角' },
];

interface DoodlePanelProps {
  editor: UseImageEditorResult;
  /** 塗鴉大小滑桿下限（依畫布動態傳入，預設 16） */
  sizeMin?: number;
  /** 塗鴉大小滑桿上限（依畫布動態傳入，預設 120） */
  sizeMax?: number;
}

/**
 * 塗鴉面板：unDraw 全庫插畫（與官網同數量），點擊縮圖加入畫布；已加入的塗鴉在此調整大小、不透明度與移除。
 */
export function DoodlePanel({
  editor,
  sizeMin = ICON_QR_SIZE_MIN,
  sizeMax = 400,
}: DoodlePanelProps) {
  const { addIconItem, iconItems, removeIconItem, updateIconItem } = editor;
  const doodleItems = iconItems.filter(i => i.source === 'doodle');
  const minSize = Math.max(ICON_QR_SIZE_MIN, sizeMin);
  const maxSize = Math.max(minSize, sizeMax);
  const [activePackId, setActivePackId] = useState<string>(
    DOODLE_PACKS[0]?.id ?? 'opendoodles'
  );
  const [searchQuery, setSearchQuery] = useState('');
  /** 主色輸入的暫存值（未 debounce 前），key = block.id */
  const [draftColors, setDraftColors] = useState<Record<string, string>>({});
  const pendingColorRef = useRef<Record<string, string>>({});
  const colorTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  const COLOR_DEBOUNCE_MS = 150;

  useEffect(() => {
    return () => {
      Object.values(colorTimeoutRef.current).forEach(t => clearTimeout(t));
    };
  }, []);

  const activePack: DoodlePack | undefined = DOODLE_PACKS.find(
    p => p.id === activePackId
  );

  const allEntriesWithPack = useMemo(() => getAllDoodleEntriesWithPack(), []);
  const searchKeyword = searchQuery.trim().toLowerCase();
  /** 搜尋塗鴉：依名稱或關鍵字篩選（與 unDraw 網站行為一致） */
  const searchResults =
    searchKeyword.length > 0
      ? allEntriesWithPack.filter(entry => {
          if (entry.name.toLowerCase().includes(searchKeyword)) return true;
          const kw = entry.keywords?.toLowerCase() ?? '';
          return kw.includes(searchKeyword);
        })
      : null;

  const apiBase = getApiBase();
  const handleAddDoodle = (entry: DoodleEntry) => {
    addIconItem({
      source: 'doodle',
      iconId: getResolvedDoodleUrl(entry, apiBase),
      size: 80,
      color: '#000000', // 預設主色黑
      opacity: 0.95,
      anchorX: 0.5,
      anchorY: 0.5,
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-600 dark:text-gray-400">
        點擊塗鴉加入畫布，於預覽區拖曳可調整位置；大小與不透明度在下方已加入的塗鴉中調整。
      </p>

      <label className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
        <Search className="h-4 w-4 shrink-0" aria-hidden />
        <input
          type="search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="搜尋塗鴉名稱（如：閱讀、創意、成功）"
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
          aria-label="搜尋塗鴉"
        />
      </label>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-600 pb-2">
        {DOODLE_PACKS.map(pack => (
          <button
            key={pack.id}
            type="button"
            onClick={() => setActivePackId(pack.id)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              activePackId === pack.id && !searchResults
                ? 'border-primary bg-primary text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
            aria-pressed={activePackId === pack.id && !searchResults}
            aria-label={`切換至 ${pack.label}`}
          >
            {pack.label}
          </button>
        ))}
      </div>

      {searchResults !== null ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            搜尋「{searchQuery.trim()}」共 {searchResults.length} 筆
          </p>
          <div
            className="flex min-h-[10.5rem] items-center gap-2 overflow-x-auto py-2 pb-2 scroll-smooth"
            style={{ scrollbarGutter: 'stable' }}
            role="list"
            aria-label="搜尋結果塗鴉列表"
          >
            {searchResults.map((entry, index) => (
              <Fragment key={`${entry.packLabel}-${entry.id}`}>
                <button
                  type="button"
                  onClick={() => handleAddDoodle(entry)}
                  className="flex h-[10.5rem] w-[10.5rem] shrink-0 items-center justify-center rounded transition-opacity hover:opacity-80 focus:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset focus:ring-offset-2"
                  title={`加入 ${entry.name}（${entry.packLabel}）`}
                  aria-label={`加入 ${entry.name}`}
                  role="listitem"
                >
                  <img
                    src={getDoodleDisplayUrl(entry, apiBase)}
                    alt=""
                    className="h-full w-full object-contain"
                    crossOrigin="anonymous"
                    loading="lazy"
                  />
                </button>
                {index < searchResults.length - 1 && (
                  <span
                    className="h-[85%] min-h-[5rem] w-px shrink-0 bg-gray-300 dark:bg-gray-500"
                    aria-hidden
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      ) : (
        activePack && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {activePack.description}
            </p>
            <div
              className="flex min-h-[10.5rem] items-center gap-2 overflow-x-auto py-2 pb-2 scroll-smooth"
              style={{ scrollbarGutter: 'stable' }}
              role="list"
              aria-label={`${activePack.label} 塗鴉列表`}
            >
              {activePack.entries.map((entry, index) => (
                <Fragment key={entry.id}>
                  <button
                    type="button"
                    onClick={() => handleAddDoodle(entry)}
                    className="flex h-[10.5rem] w-[10.5rem] shrink-0 items-center justify-center rounded transition-opacity hover:opacity-80 focus:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset focus:ring-offset-2"
                    title={`加入 ${entry.name}`}
                    aria-label={`加入 ${entry.name}`}
                    role="listitem"
                  >
                    <img
                      src={getDoodleDisplayUrl(entry, apiBase)}
                      alt=""
                      className="h-full w-full object-contain"
                      crossOrigin="anonymous"
                      loading="lazy"
                    />
                  </button>
                  {index < activePack.entries.length - 1 && (
                    <span
                      className="h-[85%] min-h-[5rem] w-px shrink-0 bg-gray-300 dark:bg-gray-500"
                      aria-hidden
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        )
      )}

      {doodleItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            已加入的塗鴉 ({doodleItems.length})
          </p>
          {doodleItems.map(block => (
            <div
              key={block.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800/50"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 dark:border-gray-600">
                {block.iconId ? (
                  <img
                    src={block.iconId}
                    alt=""
                    className="h-full w-full object-contain"
                    crossOrigin="anonymous"
                  />
                ) : null}
              </span>
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    主色
                  </span>
                  <input
                    type="color"
                    value={draftColors[block.id] ?? block.color ?? '#000000'}
                    onChange={e => {
                      const value = e.target.value;
                      setDraftColors(prev => ({ ...prev, [block.id]: value }));
                      pendingColorRef.current[block.id] = value;
                      const tid = colorTimeoutRef.current[block.id];
                      if (tid) clearTimeout(tid);
                      colorTimeoutRef.current[block.id] = setTimeout(() => {
                        updateIconItem(block.id, {
                          color: pendingColorRef.current[block.id] ?? value,
                        });
                        delete pendingColorRef.current[block.id];
                        delete colorTimeoutRef.current[block.id];
                        setDraftColors(prev => {
                          const next = { ...prev };
                          delete next[block.id];
                          return next;
                        });
                      }, COLOR_DEBOUNCE_MS);
                    }}
                    className="h-7 w-9 cursor-pointer rounded border border-gray-300 bg-transparent p-0"
                    aria-label="塗鴉主色（只替換插畫中最常出現的一色，其餘保留）"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const tid = colorTimeoutRef.current[block.id];
                      if (tid) clearTimeout(tid);
                      delete colorTimeoutRef.current[block.id];
                      delete pendingColorRef.current[block.id];
                      setDraftColors(prev => {
                        const next = { ...prev };
                        delete next[block.id];
                        return next;
                      });
                      updateIconItem(block.id, { color: '' });
                    }}
                    className="text-xs text-gray-500 hover:underline dark:text-gray-400"
                  >
                    還原原色
                  </button>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>大小 (px)</span>
                    <span className="tabular-nums">
                      {Math.max(minSize, Math.min(maxSize, block.size))} px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={minSize}
                    max={maxSize}
                    step={Math.max(1, Math.round((maxSize - minSize) / 50))}
                    value={Math.max(minSize, Math.min(maxSize, block.size))}
                    onChange={e =>
                      updateIconItem(block.id, {
                        size: Number(e.target.value),
                      })
                    }
                    className="w-full accent-primary"
                    aria-label="塗鴉大小"
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>不透明度</span>
                    <span className="tabular-nums">
                      {Math.round(block.opacity * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round(block.opacity * 100)}
                    onChange={e =>
                      updateIconItem(block.id, {
                        opacity: Number(e.target.value) / 100,
                      })
                    }
                    className="w-full accent-primary"
                    aria-label="不透明度"
                  />
                </div>
                <div>
                  <span className="mb-1 block text-xs text-gray-600 dark:text-gray-400">
                    形狀
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {DOODLE_SHAPE_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          updateIconItem(block.id, { shape: value })
                        }
                        className={`rounded px-2.5 py-1 text-xs ${
                          (block.shape ?? 'none') === value
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                        }`}
                        aria-label={`形狀：${label}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeIconItem(block.id)}
                className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700 dark:hover:text-red-400"
                aria-label="移除此塗鴉"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        塗鴉與圖示同一圖層，會繪製於畫布上、文字浮水印之前；塗鴉保留插畫原始多色。unDraw（MIT）可商用。
      </p>
    </div>
  );
}
